import { useState } from 'react';
import { ArrowLeft, MapPin, Clock, Calendar, Navigation, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

// Helper function to get the next occurrence date for an event (handles recurring events)
const getEventSortDate = (event) => {
  if (event.isRecurring && event.recurringDays && event.recurringDays.length > 0) {
    // For recurring events, find the next occurrence
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const todayDay = today.getDay();
    
    // Find the next occurrence within the next 7 days
    for (let i = 0; i < 7; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() + i);
      const checkDayName = dayNames[checkDate.getDay()];
      
      if (event.recurringDays.includes(checkDayName)) {
        return checkDate;
      }
    }
    
    // Fallback: if no occurrence in next 7 days, return today + 7 days
    const fallbackDate = new Date(today);
    fallbackDate.setDate(fallbackDate.getDate() + 7);
    return fallbackDate;
  } else {
    // For non-recurring events, use the event date
    return new Date(event.eventDate);
  }
};

export default function SearchResultsList({ events, searchQuery, userLocation, isActualUserLocation, onEventSelect, onBackToMap, showBackButton = true, onRequestLocation }) {
  const [sortBy, setSortBy] = useState('date'); // 'date' or 'distance'
  
  // Handle distance sort click - request location if not available
  const handleDistanceSortClick = () => {
    if (!isActualUserLocation && onRequestLocation) {
      toast.info('Please allow location access to sort by distance', {
        description: 'We need your location to calculate distances to events'
      });
      onRequestLocation();
    } else if (isActualUserLocation) {
      setSortBy('distance');
    }
  };
  const formatEventDateTime = (event) => {
    try {
      // Handle recurring events
      if (event.isRecurring && event.recurringDays && event.recurringDays.length > 0) {
        return `Every ${event.recurringDays.join(', ')}`;
      }
      
      // Handle one-time events
      const date = new Date(event.eventDate);
      const dateStr = date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
      return dateStr;
    } catch {
      return 'Date not set';
    }
  };

  const formatDistance = (distance) => {
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m away`;
    }
    return `${distance.toFixed(1)}km away`;
  };

  // Sort events based on user selection
  const sortedEvents = [...events].sort((a, b) => {
    if (sortBy === 'date') {
      // Sort by next occurrence date first (handles recurring events properly)
      const dateA = getEventSortDate(a);
      const dateB = getEventSortDate(b);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      
      // Then by start time (earliest first)
      if (a.startTime && b.startTime) {
        const timeA = a.startTime.split(':').map(n => parseInt(n, 10));
        const timeB = b.startTime.split(':').map(n => parseInt(n, 10));
        const minutesA = timeA[0] * 60 + timeA[1];
        const minutesB = timeB[0] * 60 + timeB[1];
        if (minutesA !== minutesB) {
          return minutesA - minutesB;
        }
      }
      
      // Then by distance
      return (a.distance || 0) - (b.distance || 0);
    } else {
      // Sort by distance first (closest first)
      const distanceCompare = (a.distance || Infinity) - (b.distance || Infinity);
      if (distanceCompare !== 0) {
        return distanceCompare;
      }
      
      // Then by date (earliest first)
      const dateA = getEventSortDate(a);
      const dateB = getEventSortDate(b);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      
      // Then by start time (earliest first)
      if (a.startTime && b.startTime) {
        const timeA = a.startTime.split(':').map(n => parseInt(n, 10));
        const timeB = b.startTime.split(':').map(n => parseInt(n, 10));
        const minutesA = timeA[0] * 60 + timeA[1];
        const minutesB = timeB[0] * 60 + timeB[1];
        return minutesA - minutesB;
      }
      
      return 0;
    }
  });

  return (
    <div className="h-full max-h-[calc(100vh-80px)] md:max-h-[calc(100vh-80px)] overflow-y-auto bg-background scrollbar-styled pb-16 md:pb-0">
      <div className="max-w-4xl mx-auto p-6 min-h-[600px]">
        {/* Back to Map Button - only show if in search mode */}
        {showBackButton && (
          <div className="mb-6">
            <Button
              onClick={onBackToMap}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Map</span>
            </Button>
          </div>
        )}

        {/* List Header with Sort Options */}
        <div className="mb-4 sm:mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl sm:text-3xl font-bold text-foreground mb-1 sm:mb-2">
            {searchQuery ? `Search Results for "${searchQuery}"` : 'All Events'}
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground">
            Found {sortedEvents.length} {sortedEvents.length === 1 ? 'event' : 'events'}
          </p>
          </div>
          
          {/* Sort Options */}
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center bg-muted rounded-lg p-1">
              <Button
                onClick={() => setSortBy('date')}
                variant={sortBy === 'date' ? 'default' : 'ghost'}
                size="sm"
                className={`h-8 px-3 text-xs ${sortBy === 'date' ? 'shadow-sm' : ''}`}
              >
                <Calendar className="h-3.5 w-3.5 mr-1" />
                Date
              </Button>
              <Button
                onClick={handleDistanceSortClick}
                variant={sortBy === 'distance' ? 'default' : 'ghost'}
                size="sm"
                className={`h-8 px-3 text-xs ${sortBy === 'distance' ? 'shadow-sm' : ''}`}
                disabled={sortBy === 'distance' && !isActualUserLocation}
              >
                <Navigation className="h-3.5 w-3.5 mr-1" />
                Distance
              </Button>
            </div>
          </div>
        </div>

        {/* Events List */}
        {sortedEvents.length > 0 ? (
          <div className="space-y-4">
            {sortedEvents.map((event) => (
              <Card 
                key={event._id} 
                className="hover:shadow-lg transition-smooth cursor-pointer border-2 hover:border-primary"
                onClick={() => onEventSelect(event)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    {/* Event Icon */}
                    <div className="text-3xl sm:text-5xl flex-shrink-0">{event.icon}</div>

                    {/* Event Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div>
                          <h3 className="text-base sm:text-xl font-bold text-foreground mb-1 sm:mb-2">
                            {event.title}
                          </h3>
                          <div className="flex flex-wrap gap-1">
                            {event.l1_category && (
                              <Badge variant="secondary" className="capitalize text-xs">
                                {event.l1_category}
                              </Badge>
                            )}
                            {event.l2_category && (
                              <Badge variant="outline" className="text-xs">
                                <span className="mr-1">{event.icon}</span>
                                {event.l2_category.replace(/_/g, ' ')}
                              </Badge>
                            )}
                            {!event.l1_category && event.category && (
                              <Badge variant="secondary" className="capitalize text-xs">
                                {event.category}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {event.distance && (
                          <div className="flex items-center space-x-1 text-sm text-primary font-medium">
                            <Navigation className="h-4 w-4" />
                            <span>{formatDistance(event.distance)}</span>
                          </div>
                        )}
                      </div>

                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {event.description}
                      </p>

                      {/* Date spans full width, Time and Location below */}
                      <div className="space-y-2">
                        {/* Date - Full Width */}
                        <div className="flex items-center space-x-2 text-sm font-semibold">
                          <Calendar className="h-4 w-4 text-primary flex-shrink-0" />
                          <span className="text-foreground">{formatEventDateTime(event)}</span>
                        </div>
                        
                        {/* Time and Location - Side by Side */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="flex items-center space-x-2 text-sm">
                            <Clock className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-foreground">{event.startTime} - {event.endTime}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-foreground truncate">{event.location}</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-lg font-bold text-accent">{event.price}</span>
                        <Button 
                          size="sm" 
                          className="gradient-primary text-primary-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventSelect(event);
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No events found
              </h3>
              <p className="text-muted-foreground">
                Try searching with different keywords or check back later
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
